import React from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import Login from "./Login";
import { CompanyState } from "../../Store/companySlice";
import { resolveAuthLogo, SHIPPED_STEPUP_LOGO } from "../../Core/branding/brandLogo";

// Shipped placeholder background. Used ONLY when the tenant has not
// yet configured their own login background in Company Settings.
const PLACEHOLDER_BG   = `${process.env.PUBLIC_URL || ""}/Images/LoginBc.png`;
// Shipped Step Up Academy logo — the absolute-last-resort fallback.
// Kept as a local const so the JSX below reads self-contained; the
// canonical export lives in Core/branding/brandLogo.ts.
const PLACEHOLDER_LOGO = SHIPPED_STEPUP_LOGO;

/**
 * Two-column login layout.
 *
 * Left column (≈60% on desktop) — the uploaded Step Up Academy artwork
 * filling the panel edge-to-edge with `object-fit: cover`. No filters, no
 * opacity change, no transforms, no blend modes.
 *
 * Right column (≈40%) — pale blue-white surface with the diagonal bands +
 * dotted pattern + soft edge glow that make the panel read as its own
 * design without competing with the login card.
 *
 * Mobile hides the left panel entirely and gives the whole viewport to the
 * pale-blue right surface + login card, per the reference.
 */
const Auth: React.FC = () => {
  // White-label: prefer the tenant's configured login background and
  // login logo from Redux (populated by useCompanyBoot from the public
  // branding endpoint). Fall back to the shipped placeholders only if
  // the tenant has not uploaded their own assets yet.
  const branding = useSelector((s: any) => (s?.company as CompanyState | undefined)?.branding);
  const bgSrc   = branding?.loginBackground || PLACEHOLDER_BG;
  // Placeholder-aware login-logo resolution. A tenant with a bad
  // `login_logo_path` (e.g. an auto-generated initial-avatar URL
  // that renders a coloured "P" square) now transparently falls
  // through to the primary `logo` (matches the sidebar identity),
  // and finally to the shipped Step Up Academy asset. See
  // Core/branding/brandLogo.ts for the placeholder patterns.
  const logoSrc = resolveAuthLogo(branding?.loginLogo, branding?.logo);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        width: "100%",
        height: "100vh",
        minHeight: "100vh",
        overflow: "hidden",
        bgcolor: "#F3F8FE",
      }}
    >
      {/* ── Left column: uploaded artwork filling the full panel ──
             `object-fit: cover` + `object-position: 46% center` on md and
             `50% center` from lg upward keeps the logo + "Empowering Minds"
             / "Building Futures" headline visible while the artwork spans
             the full 100vh with zero top/bottom gap. */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "relative",
          flex: "0 0 60%",
          width: "60%",
          height: "100vh",
          overflow: "hidden",
          bgcolor: "#FFFFFF",
          padding: 0,
          margin: 0,
        }}
      >
        <Box
          component="img"
          src={bgSrc}
          alt=""
          loading="eager"
          decoding="sync"
          {...({ fetchpriority: "high" } as any)}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            const el = e.currentTarget as HTMLImageElement;
            // Guarded single-shot — fall back once to the shipped
            // placeholder if the tenant's asset URL fails.
            if (el.src !== PLACEHOLDER_BG) el.src = PLACEHOLDER_BG;
          }}
          onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
            if (process.env.NODE_ENV !== "production") {
              const el = e.currentTarget as HTMLImageElement;
              // eslint-disable-next-line no-console
              console.log("[LoginBG]", {
                src: el.currentSrc,
                naturalWidth: el.naturalWidth,
                naturalHeight: el.naturalHeight,
                renderedWidth: el.clientWidth,
                renderedHeight: el.clientHeight,
              });
            }
          }}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            maxWidth: "none",
            maxHeight: "none",
            display: "block !important",
            visibility: "visible !important" as any,
            opacity: "1 !important" as any,
            filter: "none !important" as any,
            objectFit: "cover",
            objectPosition: "50% center",
            // Tiny scale-down to reveal the far outer edges (circular logo,
            // start of "Empowering", top-right tree, bottom-left book icon,
            // bottom-right green wave) that `cover` alone was clipping. 0.985
            // is the largest value that keeps every important element visible
            // without introducing a noticeable gap; the panel bgcolor absorbs
            // the sliver at the outer edge.
            transform: "scale(0.985)",
            transformOrigin: "center",
            margin: 0,
            padding: 0,
            // Explicit visibility / opacity / filter `!important` block any
            // stray global rule (e.g. a print-CSS leak or a broad
            // `img { opacity: 0 }` selector) from turning the artwork into
            // a blank grey area.
          }}
        />

        {/* Left-panel logo overlay removed by design — the artwork now
            shows the tree + "GROWING BRIGHT MINDS" only, and the Step Up
            Academy horizontal logo lives inside the login card on the
            right (see <Login logoSrc={…}/> below). */}
      </Box>

      {/* ── Right column: pale blue surface with subtle decorations ── */}
      <Box
        sx={{
          position: "relative",
          flex: { md: "0 0 40%" },
          width: { xs: "100%", md: "40%" },
          minHeight: { xs: "100vh", md: "100vh" },
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2.5, sm: 3, md: 4 },
          py: { xs: 3, md: 0 },
          bgcolor: "#F3F8FE",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(120% 90% at 50% 50%, rgba(20,86,160,0.08) 0%, rgba(20,86,160,0) 60%)",
            pointerEvents: "none",
            zIndex: 0,
          },
        }}
      >
        {/* Top-right diagonal bands — white over pale blue for depth. */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "-10%",
            right: "-30%",
            width: "120%",
            height: 240,
            background: "rgba(255,255,255,0.55)",
            transform: "rotate(-20deg)",
            transformOrigin: "top right",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "8%",
            right: "-35%",
            width: "125%",
            height: 180,
            background: "rgba(182,211,244,0.22)",
            transform: "rotate(-22deg)",
            transformOrigin: "top right",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        {/* Bottom-left mirror pair. */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            bottom: "-8%",
            left: "-35%",
            width: "120%",
            height: 200,
            background: "rgba(255,255,255,0.5)",
            transform: "rotate(-18deg)",
            transformOrigin: "bottom left",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            bottom: "-14%",
            left: "-40%",
            width: "125%",
            height: 160,
            background: "rgba(182,211,244,0.22)",
            transform: "rotate(-16deg)",
            transformOrigin: "bottom left",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        {/* Bottom-right masked dot pattern. */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            bottom: 24,
            right: 20,
            width: 220,
            height: 140,
            backgroundImage:
              "radial-gradient(rgba(20,86,160,0.30) 1px, transparent 1.6px)",
            backgroundSize: "12px 12px",
            maskImage:
              "radial-gradient(ellipse at bottom right, #000 0%, #000 55%, transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at bottom right, #000 0%, #000 55%, transparent 100%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: 480,
          }}
        >
          <Login logoSrc={logoSrc} />
        </Box>
      </Box>
    </Box>
  );
};

export default Auth;
